import type { LocationQuery, RouteLocationRaw } from '#vue-router'
import type { AxiosResponse } from 'axios'
import type { LoginResponse } from '@brighterly/lib-core-types/login/interfaces'

import { useCustomerStore } from '@stores/me/customer'
import { useKidStore } from '@stores/kid'
import { useDisciplineStore } from '@stores/discipline'
import { useAuthStore } from '@stores/auth'

import { useToast } from '@hooks/toast/index.hook'

import { _fetchCustomerAfterAuth } from '@utils/me/customer/fetch-customer-after-auth'
import { getHomePageRoute } from '@auth/get-home-page-route'

import { useRouter } from '#app'

interface AuthSuccessPayload {
  res: AxiosResponse<LoginResponse>
  redirectTo?: string
  queryParams?: LocationQuery
  navigate?: boolean
}

export const useAuthSuccess = async (payload: AuthSuccessPayload): Promise<RouteLocationRaw | undefined> => {
  const customerStore = useCustomerStore()
  const kidStore = useKidStore()
  const disciplineStore = useDisciplineStore()
  const authStore = useAuthStore()
  const toast = useToast()

  authStore._applyTokens({
    access_token: payload.res.data.data.access_token,
    token_type: payload.res.data.data.token_type,
    expires_in: payload.res.data.data.expires_in,
    refresh_token: payload.res.data.data.refresh_token,
  })

  const customer = await _fetchCustomerAfterAuth(customerStore, true)

  if (customer != null) {
    const router = useRouter()
    const url = payload.redirectTo || getHomePageRoute(customer)

    if (url) {
      kidStore.initFromCustomer(customer)
      await kidStore.reconcileWithCustomer(customer)
      kidStore.hydrateFromStorage()

      const kidId = kidStore.activeKidId!
      disciplineStore.initFromKidInCustomer(customer, kidId)
      disciplineStore.reconcileWithKidInCustomer(customer, kidId)
      disciplineStore.hydrateFromStorage()

      const target: RouteLocationRaw = {
        path: url,
        query: {
          notify_type: 'login',
          ...(payload?.queryParams || {}),
        },
      }

      const shouldNavigate = payload.navigate ?? true
      if (shouldNavigate) await router.push(target)

      return target
    } else {
      const msg = 'Unexpected error! useAuthVerify: child route ID not found.'
      const err = new Error(msg)
      console.error(err)

      toast.error(msg)
    }
  } else {
    const msg = 'Unexpected error! useAuthSuccess: Error fetching customer.'
    const err = new Error(msg)
    console.error(err)

    toast.error(msg)
  }
}
